import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { tenantWorkspaceRoute } from '@/app/router/routes';
import { TenantWorkspaceApi, type OnboardingReadiness, type BusinessProfile } from '@/modules/tenants/services/workspace-api';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import { OnboardingChecklist } from './parts/onboarding-checklist';
import { BusinessInfoPage } from './parts/business-info-page';
import { VerticalsPage } from './parts/verticals-page';
import { LocationsPage } from './parts/locations-page';
import { ServiceAreasPage } from './parts/service-areas-page';
import { BrandingPage } from './parts/branding-page';
import { renderIcon } from '@/shared/layout/icon-registry';
import { FiAlertCircle, FiShield, FiPackage } from 'react-icons/fi';

type WorkspaceState =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'ready'; readiness: OnboardingReadiness; profile: BusinessProfile };

export function TenantWorkspacePage(): ReactNode {
  const params = tenantWorkspaceRoute.useParams();
  const tenantId = params.tenantId as string;

  const [state, setState] = useState<WorkspaceState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      TenantWorkspaceApi.getOnboardingReadiness(),
      TenantWorkspaceApi.getProfile(),
    ])
      .then(([readiness, profile]) => {
        if (!cancelled) {
          setState({ status: 'ready', readiness, profile });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({ status: 'error', error: error instanceof Error ? error : new Error(String(error)) });
        }
      });
    return () => { cancelled = true; };
  }, [tenantId]);

  if (state.status === 'loading') {
    return (
      <AppDetailLayout
        title="Loading…"
        sections={[]}
        isLoading={true}
      />
    );
  }

  if (state.status === 'error') {
    return (
      <AppDetailLayout
        title="Error"
        sections={[]}
        error={state.error}
      />
    );
  }

  const { readiness, profile } = state;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: renderIcon('building2', 'h-4 w-4') ?? undefined, count: readiness.categories.filter(c => c.status === 'complete').length },
    { id: 'business-info', label: 'Business Info', icon: renderIcon('package', 'h-4 w-4') ?? undefined },
    { id: 'verticals', label: 'Verticals', icon: renderIcon('package', 'h-4 w-4') ?? undefined },
    { id: 'locations', label: 'Locations', icon: renderIcon('mapPin', 'h-4 w-4') ?? undefined },
    { id: 'service-areas', label: 'Service Areas', icon: renderIcon('shield', 'h-4 w-4') ?? undefined },
    { id: 'branding', label: 'Branding', icon: renderIcon('palette', 'h-4 w-4') ?? undefined },
    { id: 'onboarding', label: 'Onboarding', icon: renderIcon('alertCircle', 'h-4 w-4') ?? undefined, count: readiness.missingCount > 0 ? readiness.missingCount : undefined },
  ];

  const sections = [
    {
      id: 'overview',
      label: 'Overview',
      content: <OverviewTab readiness={readiness} profile={profile} />,
    },
    {
      id: 'business-info',
      label: 'Business Info',
      content: <BusinessInfoPage />,
    },
    {
      id: 'verticals',
      label: 'Verticals',
      content: <VerticalsPage />,
    },
    {
      id: 'locations',
      label: 'Locations',
      content: <LocationsPage />,
    },
    {
      id: 'service-areas',
      label: 'Service Areas',
      content: <ServiceAreasPage />,
    },
    {
      id: 'branding',
      label: 'Branding',
      content: <BrandingPage />,
    },
    {
      id: 'onboarding',
      label: 'Onboarding',
      content: <OnboardingChecklist readiness={readiness} />,
    },
  ];

  return (
    <AppDetailLayout
      headerVariant="accent"
      title={profile.publicName || 'Tenant Workspace'}
      subtitle={`Tenant: ${tenantId}`}
      status={readiness.overall === 'complete' ? { label: 'Ready', tone: 'positive' } : { label: `${readiness.missingCount} missing`, tone: 'caution' }}
      breadcrumbs={[
        { label: 'Platform', href: '/platform' },
        { label: 'Tenants', href: '/platform/tenants' },
        { label: profile.publicName || tenantId, href: `/platform/tenants/${tenantId}` },
        { label: 'Workspace' },
      ]}
      tabs={tabs}
      sections={sections}
      tabsInHeader={true}
    />
  );
}

function OverviewTab({ readiness, profile }: { readiness: OnboardingReadiness; profile: BusinessProfile }): ReactNode {
  const completeCategories = readiness.categories.filter(c => c.status === 'complete').length;
  const missingCategories = readiness.categories.filter(c => c.status === 'missing').length;
  const notSupportedCategories = readiness.categories.filter(c => c.status === 'not_supported').length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard title="Complete" value={completeCategories} icon={renderIcon('building2', 'h-6 w-6')} tone="positive" />
        <SummaryCard title="Missing" value={missingCategories} icon={<FiAlertCircle className="h-6 w-6" />} tone="caution" />
        <SummaryCard title="Not Supported" value={notSupportedCategories} icon={<FiShield className="h-6 w-6" />} tone="neutral" />
        <SummaryCard title="Overall" value={readiness.overall === 'complete' ? 'Ready' : 'Incomplete'} icon={<FiPackage className="h-6 w-6" />} tone={readiness.overall === 'complete' ? 'positive' : 'caution'} />
      </div>

      {/* Business Profile Summary */}
      <div className="rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium mb-4">Business Profile</h3>
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <ProfileField label="Public Name" value={profile.publicName} />
          <ProfileField label="Legal Name" value={profile.legalName || '—'} />
          <ProfileField label="Business Type" value={profile.businessType || '—'} />
          <ProfileField label="Primary Category" value={profile.primaryCategory || '—'} />
          <ProfileField label="Website" value={profile.websiteUrl || '—'} />
          <ProfileField label="Registration" value={profile.registrationStatus || '—'} />
          <ProfileField label="Primary Email" value={profile.primaryEmail || '—'} />
          <ProfileField label="Business Phone" value={profile.businessPhone || '—'} />
        </dl>
      </div>

      {/* Quick Actions */}
      <div className="rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <a href="#business-info" className="btn btn-secondary">Edit Business Info</a>
          <a href="#verticals" className="btn btn-secondary">Manage Verticals</a>
          <a href="#locations" className="btn btn-secondary">Add Location</a>
          <a href="#service-areas" className="btn btn-secondary">Configure Service Areas</a>
          <a href="#branding" className="btn btn-secondary">Update Branding</a>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, icon, tone }: { title: string; value: string | number; icon: ReactNode; tone: 'positive' | 'caution' | 'neutral' }) {
  const tones = {
    positive: 'bg-green-50 border-green-200 text-green-800',
    caution: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    neutral: 'bg-gray-50 border-gray-200 text-gray-800',
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone]}`}>
      <div className="flex items-center gap-3">
        <span className="h-6 w-6 flex items-center justify-center">{icon}</span>
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </div>
    </div>
  );
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-content-muted uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-content font-mono">{value}</dd>
    </div>
  );
}